import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ApiError } from '../../api/client'
import {
  chooseTicketTypeService,
  ticketTypeServicesQuery,
  type TicketTypeServicesRow,
} from '../../api/settingsEaseedeskTicketTypes'
import { LoadFailed } from '../../ui/LoadFailed'
import { toast } from '../../ui/notify'
import { SettingsHead } from '../../ui/SettingsParts'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Ticket types services.
 *
 * Element cu element fata de `version-3/easeedesk_settings/tickets/
 * ticket_types/services/index.php`: un rand pe fiecare tip ACTIV, o coloana pe
 * fiecare serviciu aprins la furnizor, si o bifa in fiecare incrucisare.
 *
 * In portal fiecare bifa era un `<form>` de sine statator, trimis la `change`
 * (`services/index-1.js`). Aici e o cerere pe bifa, cu acelasi inteles.
 */
function ServiceCell({
  type,
  service,
  name,
  checked,
  onSaved,
}: {
  type: TicketTypeServicesRow
  service: number
  name: string
  checked: boolean
  onSaved(): void
}) {
  const save = useMutation({
    mutationFn: (next: boolean) => chooseTicketTypeService(type.ID, service, next),
    onSuccess: () => {
      toast('Service saved.', 'Success')
      onSaved()
    },
    onError: (error) => {
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error')
      onSaved()
    },
  })

  /*
   * Bifa NU mai pluteste. `pull-right` intr-un `<td>` scotea casuta din curgere
   * si golea celula, deci randul isi pierdea inaltimea si bifa nu mai cadea pe
   * mijlocul lui. Celula e aliniata la dreapta - acolo unde e si capul coloanei
   * - si bifa ramane in flux.
   */
  return (
    <input
      type="checkbox"
      className="check_service"
      title={type.type_name + ' - ' + name}
      aria-label={type.type_name + ' - ' + name}
      checked={checked}
      disabled={save.isPending}
      onChange={(e) => save.mutate(e.target.checked)}
    />
  )
}

export function TicketTypeServicesPage() {
  const queryClient = useQueryClient()
  const page = useQuery(ticketTypeServicesQuery())

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'ticket_types_services'] })
  }

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (page.error) {
    return <LoadFailed what="The ticket type services" error={page.error} onRetry={() => void page.refetch()} />
  }
  if (!page.data) return <Spinner />

  const { types, services } = page.data

  return (
    <div>
      <SettingsHead
        strong="Ticket"
        rest="types services"
        subtitle={
          <>
            Manage <strong>services</strong> for each ticket type
          </>
        }
      />

      <table className="table table-hover dataTable">
        <thead>
          <tr>
            <th width="60"></th>
            <th style={{ textAlign: 'left' }}>Type name</th>
            {services.map((service) => (
              <th key={service.ID} style={{ textAlign: 'right' }}>
                {service.service_name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {types.map((type) => (
            <tr key={type.ID}>
              <td>
                {/* `od_console.css` da fiecarui `.badge` cerneala inchisa
                    (`--od-ink-soft`) si macheta scrie doar fundalul: prescurtarea
                    se citea inchis pe cercul colorat. In portal era alba. */}
                <span
                  className="badge"
                  style={{
                    minWidth: 23,
                    backgroundColor: type.color_identifier,
                    color: '#fff',
                    textAlign: 'center',
                  }}
                >
                  {type.short_name}
                </span>
              </td>
              <td>{type.type_name}</td>
              {services.map((service) => (
                <td key={service.ID} style={{ textAlign: 'right' }}>
                  <ServiceCell
                    type={type}
                    service={service.ID}
                    name={service.service_name}
                    checked={type.services.includes(service.ID)}
                    onSaved={reload}
                  />
                </td>
              ))}
            </tr>
          ))}
          {types.length === 0 ? (
            <tr>
              <td colSpan={services.length + 2}>
                <i className="fas fa-info-circle" aria-hidden="true"></i> No active ticket types found. You must
                activate the ticket types first.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      {services.length === 0 ? (
        <div className="alert alert-info">
          <i className="fas fa-info-circle" aria-hidden="true"></i> No service is enabled yet. Enable the services in{' '}
          <Link to="/settings/easeedesk/ticket_general_settings" data-link="ticket-general-settings">
            Settings &gt; Easeedesk &gt; Settings
          </Link>{' '}
          (section Services), then press REBUILD there.
        </div>
      ) : null}
    </div>
  )
}
